import React, { useState, useMemo, useImperativeHandle } from "react";
import { Input, Row, Checkbox, Modal, AutoComplete, Form, Button, Select, Col, Divider, message } from "antd";
import { useThrottleFn, useRequest } from "ahooks";
import _ from "lodash";
import { StarFilled } from "@ant-design/icons";
import { isNonnegativeValidator, omit } from "@/utils";
import { FormInstance } from "antd";
import { getConsigneeList, submitInaccurateRecognitionFeedback } from "@/services/api/manualOrder";
import InputNumber from "@/components/InputNumber";
import ConsigneeManagementModal from "../ConsigneeManagementModal";
import { useModalStore } from "@/hooks/Modal/hooks";
import RecognizeAddressButton from "../RecognizeAddressButton";
import ManualOrderConsigneeDTO from "@/dto/manualOrder/ManualOrderConsigneeDTO";
import RecognizeAddressTextArea from "../RecognizeAddressTextArea";
import MobileFormItem from "../MobileFormItem";
import { ConsigneeInfoFormInstance, ConsigneeInfoFormValues } from "./type";
import QuickRecognizeAddressButton from "../QuickRecognizeAddressButton";
import { useGlobalStore, useOrderMarkStore } from "@/store/hooks";
import BatchRecognizeAddressButton from "../BatchRecognizeAddressButton";
import EcSelect from "@/components/EcSelect";
import type { EcSelectValue } from "@/components/EcSelect/type";
import { getAntdMarkIcon } from "@/services/api/setting/orderMark";
import { observer } from "mobx-react-lite";
import SectionTitle from "../SectionTitle";

interface ConsigneeInfoFormProps {
    shopId?: number;
    hideSmartButton?: boolean;
    privacyProtection?: boolean;
    onResetFullForm?: () => void;
}

interface AutoCompleteDateSourceType {
    label: string;

    value: string;

    originalValue: ManualOrderConsigneeDTO;
}

function getCodAmountsValidate(form: FormInstance<ConsigneeInfoFormValues>) {
    return {
        validator() {
            const { isCodCheckbox, codAmounts } = form.getFieldsValue();

            if (isCodCheckbox) {
                if (Number.isNaN(Number(codAmounts)) || Number(codAmounts) < 0) {
                    return Promise.reject("代收货款只能是大于0的数字");
                }
            }

            return Promise.resolve();
        },
    };
}

function filterUndefined(val) {
    if (!val) return "";

    return val;
}

interface ManualOrderMarkSelectProps {
    value?: number;
    onChange?: (value?: number) => void;
}

const ManualOrderMarkSelect: React.FC<ManualOrderMarkSelectProps> = observer(function ManualOrderMarkSelect(props) {
    const { value, onChange } = props;
    const orderMarkStore = useOrderMarkStore();

    const data = orderMarkStore.listSkipDeleted.map((orderMark) => {
        const { id, remark, icon, color } = orderMark;
        const IconComponent = getAntdMarkIcon(icon);

        return {
            value: id,
            label: (
                <span className="ml5">
                    {IconComponent ? <IconComponent style={{ color }} className="mr5" /> : null}
                    {remark}
                </span>
            ),
        };
    });

    const handleChange = (markId: EcSelectValue | EcSelectValue[]) => {
        if (Array.isArray(markId)) return;

        onChange?.(markId ? Number(markId) : undefined);
    };

    return (
        <EcSelect
            placeholder="选择标记"
            value={value as any}
            onChange={handleChange}
            onOpenChange={(visible) => {
                if (visible) {
                    orderMarkStore.loadList();
                }
            }}
            data={data}
            loading={orderMarkStore.loading}
            separator={null}
            groupOptions={{ groupBy: () => "", hideGroupTitle: true }}
            buttonProps={{
                size: "small",
                style: {
                    width: 90,
                    textAlign: "left",
                },
            }}
        />
    );
});

export default React.forwardRef<ConsigneeInfoFormInstance, ConsigneeInfoFormProps>(
    ({ shopId, hideSmartButton, privacyProtection, onResetFullForm }, ref) => {
        const [autoCompleteDateSource, setAutoCompleteDateSource] = useState<AutoCompleteDateSourceType[]>([]);
        const [formRef] = Form.useForm<ConsigneeInfoFormValues>();
        const [smartText, updateSmartText] = useState("");
        const globalStore = useGlobalStore();

        const fhyAreas = globalStore.areaStore.fhyAreas;

        useRequest(globalStore.areaStore.ensureFhyArea);

        const modalStore = useModalStore();

        const { runAsync: getConsigneeListRun } = useRequest(getConsigneeList, {
            manual: true,
        });

        const { runAsync: submitFeedback, loading: feedbackLoading } = useRequest(submitInaccurateRecognitionFeedback, {
            manual: true,
        });

        async function handleInaccurateFeedback() {
            if (!shopId || !smartText?.trim()) {
                Modal.error({ title: "请先填写收货人信息" });

                return;
            }

            await submitFeedback(shopId, smartText);

            message.success("信息提交成功，感谢您的反馈");
        }

        const { run: throttleAutoCompleteSearch } = useThrottleFn(
            (value: string) => {
                handleAutoCompleteSearch(value);
            },
            { wait: 500 },
        );

        const handleQuickRecognizeAddress = (data: ManualOrderConsigneeDTO, text: string) => {
            handleRecognizeAddress(data);

            updateSmartText(text);
        };

        const handleRecognizeAddress = async (data: ManualOrderConsigneeDTO) => {
            const { name: consigneeName, mobile, street, telephone, provinceCode, cityCode, districtCode } = data || {};

            formRef.setFieldsValue({
                consigneeName,
                mobile,
                telephone,
                street,
                provinceCode,
                cityCode,
                districtCode,
            });

            /** 2024/05/23 */
            /** 立伟贸易有限公司 021-58644588上海市上海市浦东新区茂祥路258号4号LW库58644588(前台) */
            /** 经过下面步骤后，没有将手机号的校验信息去除 */
            /** 1.识别 2.去掉固化 3.保存 4.再次识别 */

            /** 需要手动触发校验去除错误提示 */
            if (telephone) {
                formRef.validateFields(["mobile"]);
            }
        };

        const handleAutoComplete = (value) => {
            const { setFieldsValue } = formRef;

            const consignee = autoCompleteDateSource[value].originalValue;

            const { provinceCode, cityCode, districtCode, name } = consignee;

            setFieldsValue({
                ...consignee,
                provinceCode,
                cityCode,
                districtCode,
                consigneeName: name,
            });
        };

        const handleAutoCompleteSearch = async (value: string) => {
            const consigneeList = await getConsigneeListRun({
                consigneeName: value,
                pageNo: 0,
                pageSize: 20,
                shopId,
            });

            const _autoCompleteDateSource = consigneeList?.items.map((item, index) => {
                const label = `${item.name} ${filterUndefined(item.province)}${filterUndefined(
                    item.city,
                )}${filterUndefined(item.district)} ${filterUndefined(item.street)}`;

                return {
                    label,

                    value: String(index),

                    originalValue: item,
                };
            });

            setAutoCompleteDateSource(_autoCompleteDateSource || []);
        };

        const reset = () => {
            formRef.resetFields();

            updateSmartText("");
        };

        const getFormValues = async () => {
            try {
                const { provinceCode, cityCode, districtCode } = await formRef.validateFields();

                return {
                    ...omit(formRef.getFieldsValue(), "area"),
                    provinceCode,
                    cityCode,
                    districtCode,
                } as Omit<ConsigneeInfoFormValues, "area">;
            } catch (e) {
                !hideSmartButton &&
                    Modal.error({
                        title: "填写有误，请改正后再保存",
                    });

                return Promise.reject(e);
            }
        };

        useImperativeHandle(ref, () => ({
            reset,
            getFormValues,
            setFieldsValue: formRef.setFieldsValue,
        }));

        const dataSource: Omit<AutoCompleteDateSourceType, "originalValue">[] = useMemo(
            () => autoCompleteDateSource.map((item) => omit(item, ["originalValue"])),
            [autoCompleteDateSource],
        );

        return (
            <>
                <SectionTitle title="收货人信息" />
                {hideSmartButton && (
                    <div className="flex error ">
                        <p>免责声明：</p>
                        <p>
                            由于收货人信息十分重要。录入或导入完成后请检查收货人信息是否正确。
                            <br />
                            发货易软件不承担因收货人信息错误导致快递配送错误造成的损失
                        </p>
                    </div>
                )}
                {!hideSmartButton && (
                    <>
                        <Row className="mt10">
                            <Col span={24}>
                                <RecognizeAddressTextArea smartText={smartText} onTextChange={updateSmartText} />

                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <div>
                                        <RecognizeAddressButton
                                            value={smartText}
                                            shopId={shopId}
                                            onChange={handleRecognizeAddress}
                                        />

                                        {!import.meta.env.VITE_IS_QN ? (
                                            <QuickRecognizeAddressButton
                                                shopId={shopId}
                                                onRecognizeAddress={handleQuickRecognizeAddress}
                                            />
                                        ) : null}

                                        <BatchRecognizeAddressButton
                                            shopId={shopId}
                                            value={smartText}
                                            onResetFullForm={onResetFullForm}
                                        />
                                    </div>

                                    <Button type="link" loading={feedbackLoading} onClick={handleInaccurateFeedback}>
                                        地址识别不准确，立即反馈
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                        <Divider className="mt10 mb3"></Divider>
                    </>
                )}

                <Form colon={false} form={formRef} initialValues={{ shippingCost: 0, weight: 0 }}>
                    <Row>
                        <Form.Item
                            label="姓名"
                            extra={
                                !hideSmartButton && (
                                    <Button
                                        type="link"
                                        icon={<StarFilled />}
                                        onClick={() => modalStore.openModal(ConsigneeManagementModal, { shopId })}
                                        style={{ height: "auto", fontSize: 12, padding: 0 }}
                                    >
                                        管理常用收货人
                                    </Button>
                                )
                            }
                            name="consigneeName"
                            className="fs12"
                            required={false}
                            rules={[
                                {
                                    required: true,
                                    message: "收货人姓名不能为空",
                                },
                            ]}
                        >
                            <AutoComplete
                                size="small"
                                options={dataSource}
                                style={{ width: 120 }}
                                onFocus={(e: any) => {
                                    !hideSmartButton && handleAutoCompleteSearch(e.target.value);
                                }}
                                onSelect={!hideSmartButton ? handleAutoComplete : undefined}
                                showSearch={{
                                    onSearch: !hideSmartButton ? throttleAutoCompleteSearch : undefined,
                                }}
                                popupMatchSelectWidth={500}
                            />
                        </Form.Item>
                        <MobileFormItem privacyProtection={privacyProtection} />
                        <Form.Item label="固话" name="telephone" style={{ margin: 0, marginLeft: 10 }}>
                            <Input style={{ width: 120 }} size="small" />
                        </Form.Item>
                    </Row>
                    <Row>
                        <Form.Item noStyle shouldUpdate>
                            {(form: FormInstance<ConsigneeInfoFormValues>) => {
                                return (
                                    <Form.Item
                                        label="省份"
                                        name="provinceCode"
                                        initialValue=""
                                        rules={[{ required: true }]}
                                        required={false}
                                    >
                                        <Select
                                            size="small"
                                            showSearch={{
                                                optionFilterProp: "label",
                                            }}
                                            style={{ width: 120 }}
                                            popupMatchSelectWidth={false}
                                            onChange={() => {
                                                form.setFields([
                                                    { name: "cityCode", value: "" },
                                                    { name: "districtCode", value: "" },
                                                ]);
                                            }}
                                            options={[
                                                {
                                                    value: "",
                                                    label: "- 选择省份 -",
                                                },
                                                ...(fhyAreas.map((province) => {
                                                    const { id, name } = province;
                                                    return {
                                                        value: id,
                                                        key: id,
                                                        label: name,
                                                    };
                                                }) ?? []),
                                            ]}
                                        />
                                    </Form.Item>
                                );
                            }}
                        </Form.Item>

                        <Form.Item<ConsigneeInfoFormValues>
                            noStyle
                            shouldUpdate={(prev, next) => prev.provinceCode !== next.provinceCode}
                        >
                            {(form: FormInstance<ConsigneeInfoFormValues>) => {
                                const { provinceCode } = form.getFieldsValue();

                                const cities = globalStore.areaStore.fhyAreaMap.get(provinceCode)?.children;

                                return (
                                    <Form.Item
                                        label="城市"
                                        name="cityCode"
                                        initialValue=""
                                        style={{ margin: 0, marginLeft: 10 }}
                                        rules={[{ required: true }]}
                                        required={false}
                                    >
                                        <Select
                                            size="small"
                                            showSearch={{
                                                optionFilterProp: "label",
                                            }}
                                            style={{ width: 120 }}
                                            popupMatchSelectWidth={false}
                                            onChange={() => {
                                                form.setFields([{ name: "districtCode", value: "" }]);
                                            }}
                                            options={[
                                                {
                                                    value: "",
                                                    label: "- 选择城市 -",
                                                },
                                                ...(cities?.map((city) => {
                                                    const { id, name } = city;
                                                    return {
                                                        value: id,
                                                        key: id,
                                                        label: name,
                                                    };
                                                }) ?? []),
                                            ]}
                                        />
                                    </Form.Item>
                                );
                            }}
                        </Form.Item>
                        <Form.Item<ConsigneeInfoFormValues>
                            noStyle
                            shouldUpdate={(prev, next) => prev.cityCode !== next.cityCode}
                        >
                            {(form: FormInstance<ConsigneeInfoFormValues>) => {
                                const { cityCode } = form.getFieldsValue();

                                const districts = globalStore.areaStore.fhyAreaMap.get(cityCode)?.children;

                                return (
                                    <Form.Item
                                        style={{ margin: 0, marginLeft: 10 }}
                                        label="区/县"
                                        name="districtCode"
                                        initialValue=""
                                        className="ml10"
                                        required={false}
                                        rules={[
                                            {
                                                required: true,
                                                message: (
                                                    <span style={{ display: "inline-block", width: 100 }}>
                                                        请输入区/县
                                                        <br />
                                                        <a
                                                            href="https://qw15p3s8hv.feishu.cn/wiki/W5SZwHO3BieXxckqu5kc8cKHnRR?from=from_copylink"
                                                            target="_blank"
                                                        >
                                                            点击查看如何识别地址的区县或街道
                                                        </a>
                                                    </span>
                                                ),
                                            },
                                        ]}
                                    >
                                        <Select
                                            showSearch={{
                                                optionFilterProp: "label",
                                            }}
                                            style={{ width: 120 }}
                                            size="small"
                                            popupMatchSelectWidth={false}
                                            options={[
                                                {
                                                    value: "",
                                                    label: "- 选择区/县 -",
                                                },
                                                ...(districts?.map((city) => {
                                                    const { id, name } = city;
                                                    return {
                                                        value: id,
                                                        key: id,
                                                        label: name,
                                                    };
                                                }) ?? []),
                                            ]}
                                        />
                                    </Form.Item>
                                );
                            }}
                        </Form.Item>
                    </Row>
                    <Row>
                        <Form.Item
                            label={
                                <div style={{ width: 28, wordBreak: "break-all", whiteSpace: "pre-line" }}>
                                    详细地址
                                </div>
                            }
                            name="street"
                            required={false}
                            rules={[
                                {
                                    required: true,
                                    message: "详细地址不能为空",
                                },
                            ]}
                        >
                            <Input.TextArea rows={3} style={{ width: 480 }} size="small" />
                        </Form.Item>
                    </Row>
                    <Row>
                        <Form.Item label="备注" name="sellerRemarks" style={{ margin: 0, marginBottom: 10 }}>
                            <Input.TextArea rows={3} placeholder="卖家备注" style={{ width: 480 }} size="small" />
                        </Form.Item>
                    </Row>
                    <Row>
                        <Form.Item label="留言" name="buyerRemarks">
                            <Input.TextArea rows={3} style={{ width: 480 }} size="small" />
                        </Form.Item>
                    </Row>
                    <Row>
                        <Form.Item label="昵称" name="buyerUsername">
                            <Input style={{ width: 120 }} placeholder="买家昵称" size="small" />
                        </Form.Item>
                        <Form.Item label="订单编号" name="originalId" style={{ margin: 0, marginLeft: 10 }}>
                            <Input style={{ width: 270 }} placeholder="原平台的订单编号" size="small" />
                        </Form.Item>
                    </Row>
                    <Row>
                        <Form.Item label="折扣">
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <Form.Item name="discounts" noStyle>
                                    <InputNumber precision={2} style={{ width: 80 }} size="small" min={0} />
                                </Form.Item>
                                <Form.Item name="isCodCheckbox" valuePropName="checked" noStyle>
                                    <Checkbox style={{ marginLeft: 8 }}>代收货款订单</Checkbox>
                                </Form.Item>
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prev: ConsigneeInfoFormValues, next: ConsigneeInfoFormValues) =>
                                        prev.isCodCheckbox !== next.isCodCheckbox
                                    }
                                >
                                    {(form: FormInstance<ConsigneeInfoFormValues>) => {
                                        const { isCodCheckbox } = form.getFieldsValue();

                                        if (isCodCheckbox) {
                                            return (
                                                <>
                                                    <Form.Item
                                                        name="codAmounts"
                                                        rules={[getCodAmountsValidate]}
                                                        style={{ margin: 0, marginLeft: 8 }}
                                                    >
                                                        <Input suffix="元" style={{ width: 120 }} size="small" />
                                                    </Form.Item>

                                                    <span className="error small ml5" style={{ lineHeight: "32px" }}>
                                                        订单金额优先使用代收货款
                                                    </span>
                                                </>
                                            );
                                        }

                                        return null;
                                    }}
                                </Form.Item>
                            </div>
                        </Form.Item>
                    </Row>
                    <Row>
                        <Form.Item
                            label="运费"
                            name="shippingCost"
                            rules={[
                                {
                                    validator: isNonnegativeValidator,
                                },
                            ]}
                            style={{ margin: 0 }}
                        >
                            <InputNumber style={{ width: 120 }} suffix="元" min={0} size="small" />
                        </Form.Item>
                        <Form.Item
                            label="重量"
                            name="weight"
                            rules={[
                                {
                                    validator: isNonnegativeValidator,
                                },
                            ]}
                            style={{ margin: 0, marginLeft: 10 }}
                        >
                            <InputNumber style={{ width: 120 }} suffix="kg" min={0} size="small" />
                        </Form.Item>
                        <Form.Item label="订单标记" name="markId" style={{ margin: 0, marginLeft: 10 }}>
                            <ManualOrderMarkSelect />
                        </Form.Item>
                    </Row>
                </Form>
            </>
        );
    },
);
